import i18next from 'i18next';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { getPastProductMixByDate, getPastProductMixByStore } from '@services/forecast';

export const DEFAULT_CATEGORY = 'none value';

/**
 * Format product table data
 */
const formatProductTableData = (pastMixByPeriod, periods, turnoverByDateByPeriod) => {
  const totalTurnoverByPeriod = periods.reduce((acc, { key }) => {
    acc[key] = Object.values(turnoverByDateByPeriod[key] || {}).reduce(
      (sum, val) => sum + (val || 0),
      0,
    );
    return acc;
  }, {});

  // Aggregate data by product across all dates
  const productsMap = periods.reduce((acc, { key }) => {
    (pastMixByPeriod[key] || []).forEach(
      ({ productId, productName, productCategory, quantity, turnoverExcludingTaxes }) => {
        if (!acc[productId]) {
          acc[productId] = {
            id: productId,
            name: productName,
            category:
              productCategory === DEFAULT_CATEGORY
                ? i18next.t('GENERAL.NONE_VALUE')
                : productCategory,
            units: {},
            turnoverExcludingTaxes: {},
            mix: {},
          };
        }

        acc[productId].units[key] = (acc[productId].units[key] || 0) + (quantity || 0);
        acc[productId].turnoverExcludingTaxes[key] =
          (acc[productId].turnoverExcludingTaxes[key] || 0) + (turnoverExcludingTaxes || 0);
      },
    );
    return acc;
  }, {});

  // Calculate mix for each product in each period
  return Object.values(productsMap).map((row) => {
    periods.forEach(({ key }) => {
      const turnover = row.turnoverExcludingTaxes[key];
      const totalTurnover = totalTurnoverByPeriod[key];

      row.mix[key] =
        totalTurnover > 0 && turnover !== undefined ? (turnover / totalTurnover) * 100 : null;
    });
    return row;
  });
};

/**
 * Format aggregated product data
 */
const formatProductData = (productTableData, periods) =>
  productTableData.reduce(
    (acc, row) => {
      periods.forEach(({ key }) => {
        acc.units[key] += row.units[key] || 0;
        acc.turnoverExcludingTaxes[key] += row.turnoverExcludingTaxes[key] || 0;
      });
      return acc;
    },
    {
      units: periods.reduce((acc, { key }) => ({ ...acc, [key]: 0 }), {}),
      turnoverExcludingTaxes: periods.reduce((acc, { key }) => ({ ...acc, [key]: 0 }), {}),
    },
  );

/**
 * Format chart data
 */
const formatChartData = (pastMixByPeriod, turnoverByDateByPeriod, periods, currentPeriod) => {
  const turnoverByDate = { ...turnoverByDateByPeriod.current, ...turnoverByDateByPeriod.previous };
  const pastMixByDate = [...pastMixByPeriod.current, ...pastMixByPeriod.previous];

  const processedDataByDate = pastMixByDate.reduce((acc, item) => {
    const { date, productId, quantity, turnoverExcludingTaxes } = item;
    if (!acc[date]) acc[date] = { units: {}, turnoverExcludingTaxes: {}, mix: {} };

    const totalTurnoverForDate = turnoverByDate[date] || 0;
    acc[date].units[productId] = quantity || 0;
    acc[date].turnoverExcludingTaxes[productId] = turnoverExcludingTaxes || 0;
    acc[date].mix[productId] =
      totalTurnoverForDate > 0 ? (turnoverExcludingTaxes / totalTurnoverForDate) * 100 : null;
    return acc;
  }, {});

  const maxDays = Math.max(
    ...periods.map(({ range }) => range.endDate.diff(range.startDate, 'days')),
  );

  const data = Array.from({ length: maxDays + 1 }, (_, dayIndex) => {
    const item = {
      index: maxDays - dayIndex,
      date: currentPeriod.endDate.clone().subtract(dayIndex, 'days'),
      formattedDates: {},
    };

    periods.forEach(({ key, range }) => {
      const targetDate = range.endDate.clone().subtract(dayIndex, 'days');
      const dateString = targetDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);
      const daysInRange = range.endDate.diff(range.startDate, 'days');

      item.formattedDates[key] = targetDate.format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR);
      item[key] =
        dayIndex <= daysInRange
          ? processedDataByDate[dateString] || { units: {}, turnoverExcludingTaxes: {}, mix: {} }
          : 0;
    });

    return item;
  });

  return data.sort((a, b) => a.index - b.index);
};

/**
 * Fetch product time series data
 */
export const fetchProductTimeSeriesData = async (selectedStores, currentPeriod, previousPeriod) => {
  const storeIds = selectedStores.map((store) => store.id);

  const periods = [
    { key: 'current', range: currentPeriod },
    { key: 'previous', range: previousPeriod },
  ];

  // Make two API calls (one for each period) and combine responses
  const [currentResponse, previousResponse] = await Promise.all(
    periods.map(({ range }) =>
      getPastProductMixByDate(
        storeIds,
        range.startDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        range.endDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      ),
    ),
  );

  const pastMixByPeriod = { current: currentResponse.pastMix, previous: previousResponse.pastMix };
  const turnoverByDateByPeriod = {
    current: currentResponse.totalTurnoverExcludingTaxes,
    previous: previousResponse.totalTurnoverExcludingTaxes,
  };

  const productTableData = formatProductTableData(pastMixByPeriod, periods, turnoverByDateByPeriod);
  const productData = formatProductData(productTableData, periods);
  const chartData = formatChartData(
    pastMixByPeriod,
    turnoverByDateByPeriod,
    periods,
    currentPeriod,
  );

  return {
    productData,
    productTableData,
    chartData,
  };
};

/**
 * Fetch all products data for multiple stores (for comparison chart caching)
 */
export const fetchAllProductsForStores = async (selectedStores, currentPeriod, previousPeriod) => {
  const storeIds = selectedStores.map((store) => store.id);
  const comparisonData = {};

  // Fetch data for all stores in a single call per period
  const periods = [
    { key: 'current', range: currentPeriod },
    { key: 'previous', range: previousPeriod },
  ];

  const [currentResponse, previousResponse] = await Promise.all(
    periods.map(({ range }) =>
      getPastProductMixByStore(
        storeIds,
        range.startDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        range.endDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      ),
    ),
  );

  const mixByPeriod = { current: currentResponse.pastMix, previous: previousResponse.pastMix };
  const turnoverByStoreByPeriod = {
    current: currentResponse.totalTurnoverExcludingTaxes,
    previous: previousResponse.totalTurnoverExcludingTaxes,
  };

  // Group products by store and period
  const productsByStoreAndPeriod = {};

  periods.forEach(({ key }) => {
    mixByPeriod[key].forEach((item) => {
      const storeId = item.storeId;
      const productId = item.productId;

      if (!productsByStoreAndPeriod[storeId]) {
        productsByStoreAndPeriod[storeId] = {};
      }
      if (!productsByStoreAndPeriod[storeId][productId]) {
        productsByStoreAndPeriod[storeId][productId] = {
          id: productId,
          name: item.productName,
          category: item.productCategory,
          units: { current: 0, previous: 0 },
          turnoverExcludingTaxes: { current: 0, previous: 0 },
          mix: { current: null, previous: null },
        };
      }

      productsByStoreAndPeriod[storeId][productId].units[key] += item.quantity || 0;
      productsByStoreAndPeriod[storeId][productId].turnoverExcludingTaxes[key] +=
        item.turnoverExcludingTaxes || 0;
    });
  });

  // Calculate mix for each product in each store
  Object.keys(productsByStoreAndPeriod).forEach((storeId) => {
    periods.forEach(({ key }) => {
      const storeTotal = turnoverByStoreByPeriod[key][storeId] || 0;
      Object.values(productsByStoreAndPeriod[storeId]).forEach((product) => {
        if (storeTotal > 0 && product.turnoverExcludingTaxes[key] > 0) {
          product.mix[key] = (product.turnoverExcludingTaxes[key] / storeTotal) * 100;
        }
      });
    });
  });

  // Organize data by store
  selectedStores.forEach((store) => {
    const storeId = store.id;
    const storeProducts = productsByStoreAndPeriod[storeId] || {};

    comparisonData[storeId] = {
      storeId: store.id,
      storeName: store.name,
      products: storeProducts,
    };
  });

  return comparisonData;
};
